import React, { useEffect, useRef } from "react";
import IPhoneMockup from "@/components/IPhoneMockup";
import { APP_SCREENS, APP_SCREENS_ALT } from "@/lib/assets";
import useClusterScrollTilt from "@/lib/useClusterScrollTilt";

/**
 * HeroIPhoneCluster — clean Apple-style 3-iPhone composition.
 * Each phone is absolutely positioned with explicit inline transforms.
 *
 * Layout (desktop):
 *  - MainPhone (center, z=30, scale 1.0)
 *  - LeftBackPhone (behind-left, z=20, scale 0.88)
 *  - RightBackPhone (behind-right, z=18, scale 0.88)
 */
export default function HeroIPhoneCluster({ reducedMotion = false }) {
  const stageRef = useRef(null);
  useClusterScrollTilt(stageRef);

  useEffect(() => {
    if (reducedMotion) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 1024px)").matches) return;

    const el = stageRef.current;
    if (!el) return;
    let rafId = 0;
    let targetX = 0, targetY = 0, currentX = 0, currentY = 0;
    const onMove = (ev) => {
      const dx = (ev.clientX - window.innerWidth / 2) / window.innerWidth;
      const dy = (ev.clientY - window.innerHeight / 2) / window.innerHeight;
      targetX = Math.max(-4, Math.min(4, dx * 4));
      targetY = Math.max(-2, Math.min(2, -dy * 2));
    };
    const tick = () => {
      currentX += (targetX - currentX) * 0.06;
      currentY += (targetY - currentY) * 0.06;
      el.style.setProperty("--cluster-rx", `${currentY}deg`);
      el.style.setProperty("--cluster-ry", `${currentX}deg`);
      rafId = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    rafId = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafId);
    };
  }, [reducedMotion]);

  // Explicit inline transforms — guarantees no CSS cascade issues.
  const baseAbsCenter = {
    position: "absolute",
    left: "50%",
    top: "50%",
  };

  return (
    <div className="iphone-cluster-stage" ref={stageRef} data-testid="hero-iphone-cluster">
      <div className="iphone-cluster-float">
        {/* LEFT BACK — Restaurant profile (LARGE DESKTOP ≥1380px) */}
        <div
          className="cluster-phone cluster-phone-left hidden min-[1380px]:block"
          style={{
            ...baseAbsCenter,
            transform:
              "translate(calc(-50% - 230px), calc(-50% + 52px)) rotateY(16deg) rotateX(2deg) rotateZ(-4deg) scale(0.8)",
            zIndex: 20,
            filter: "brightness(0.93)",
          }}
          data-slot="left-back"
        >
          <IPhoneMockup
            width={270}
            image={APP_SCREENS.restaurant}
            alt={APP_SCREENS_ALT.restaurant}
            testId="phone-back-left"
            priority
          />
        </div>

        {/* RIGHT BACK — Quality profile (LARGE DESKTOP ≥1380px) */}
        <div
          className="cluster-phone cluster-phone-right hidden min-[1380px]:block"
          style={{
            ...baseAbsCenter,
            transform:
              "translate(calc(-50% + 230px), calc(-50% + 62px)) rotateY(-16deg) rotateX(2deg) rotateZ(4deg) scale(0.8)",
            zIndex: 18,
            filter: "brightness(0.93)",
          }}
          data-slot="right-back"
        >
          <IPhoneMockup
            width={270}
            image={APP_SCREENS.quality}
            alt={APP_SCREENS_ALT.quality}
            testId="phone-back-right"
            priority
          />
        </div>

        {/* FRONT MAIN — Berlin map */}
        <div
          className="cluster-phone cluster-phone-main"
          style={{
            ...baseAbsCenter,
            transform: "translate(-50%, -50%) rotateY(-6deg) rotateX(2deg)",
            zIndex: 30,
          }}
          data-slot="main"
        >
          <IPhoneMockup
            width={310}
            image={APP_SCREENS.map}
            alt={APP_SCREENS_ALT.map}
            testId="phone-front"
            priority
          />
        </div>
      </div>
    </div>
  );
}
